/**
 * Curriculum Detail Component
 * Shows selected curriculum with modules and generation controls
 */

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import {
    Sparkles,
    FileText,
    CheckCircle,
    Clock,
    Loader2,
    BookOpen,
    Layers,
    Target,
    Eye,
    RefreshCw,
    Send,
    Trash2
} from 'lucide-react';

const CurriculumDetail = ({
    curriculum,
    onGenerate,
    onRegenerate,
    onPublish,
    onDelete,
    onFileUpload,
    onViewTopic,
    getStatusColor
}) => {
    if (!curriculum) {
        return (
            <Card className="h-full flex items-center justify-center min-h-[400px]">
                <CardContent className="text-center">
                    <BookOpen className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-30" />
                    <p className="text-muted-foreground">
                        Select a curriculum to view and edit
                    </p>
                </CardContent>
            </Card>
        );
    }

    const isGenerating = curriculum.generation_status === 'generating' || curriculum.generation_status === 'regenerating';

    return (
        <Card>
            <CardHeader>
                <div className="flex items-start justify-between">
                    <div>
                        <CardTitle>{curriculum.title}</CardTitle>
                        <CardDescription>{curriculum.description}</CardDescription>
                    </div>
                    <div className="flex gap-2">
                        {curriculum.status === 'pending_review' && (
                            <Button 
                                onClick={() => onPublish(curriculum.id)}
                                className="gap-2"
                            >
                                <Send className="h-4 w-4" />
                                Publish
                            </Button>
                        )}
                        <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => onDelete(curriculum.id)}
                        >
                            <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                    </div>
                </div>
                
                <div className="flex flex-wrap gap-2 mt-4">
                    <Badge variant="outline">
                        <Target className="h-3 w-3 mr-1" />
                        {curriculum.target_audience}
                    </Badge>
                    <Badge variant="outline">
                        <Clock className="h-3 w-3 mr-1" />
                        {curriculum.estimated_duration}
                    </Badge>
                    <Badge className={getStatusColor(curriculum.status)}>
                        {curriculum.status}
                    </Badge>
                </div>
            </CardHeader>
            
            <CardContent>
                {/* Generation Controls for Draft */}
                {curriculum.status === 'draft' && (
                    <div className="mb-6 p-4 rounded-lg border-2 border-dashed border-primary/30 bg-primary/5">
                        <h4 className="font-medium mb-3">Setup Curriculum</h4>
                        
                        {/* Reference Docs */}
                        <div className="mb-4">
                            <Label className="text-sm">Reference Documents (Optional)</Label>
                            <p className="text-xs text-muted-foreground mb-2">
                                Upload documents to help AI generate better content
                            </p>
                            <div className="flex gap-2">
                                <Input
                                    type="file"
                                    accept=".pdf,.doc,.docx,.txt,.md"
                                    onChange={(e) => onFileUpload(e, curriculum.id)}
                                    className="flex-1"
                                />
                            </div>
                            {curriculum.reference_documents?.length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-2">
                                    {curriculum.reference_documents.map(doc => (
                                        <Badge key={doc.id} variant="secondary" className="gap-1">
                                            <FileText className="h-3 w-3" />
                                            {doc.filename}
                                        </Badge>
                                    ))}
                                </div>
                            )}
                        </div>
                        
                        <Button 
                            onClick={() => onGenerate(curriculum.id)}
                            className="w-full gap-2"
                            disabled={isGenerating}
                        >
                            {isGenerating ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <Sparkles className="h-4 w-4" />
                            )}
                            Generate Curriculum Content
                        </Button>
                        
                        {isGenerating && (
                            <div className="mt-3">
                                <Progress value={curriculum.generation_progress || 0} className="h-2" />
                                <p className="text-sm text-center text-muted-foreground mt-2">
                                    {curriculum.generation_message || 'Generating...'}
                                </p>
                            </div>
                        )}
                    </div>
                )}
                
                {/* Modules & Topics */}
                {curriculum.modules?.length > 0 && (
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <h4 className="font-medium">Modules & Topics</h4>
                            <Button 
                                variant="outline" 
                                size="sm"
                                className="gap-2"
                                onClick={() => onRegenerate(curriculum.id)}
                                disabled={isGenerating}
                                data-testid="regenerate-content-btn"
                            >
                                {isGenerating ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    <RefreshCw className="h-4 w-4" />
                                )}
                                Regenerate All Content
                            </Button>
                        </div>
                        
                        {isGenerating && (
                            <div className="p-3 rounded-lg bg-primary/5 border border-primary/20">
                                <Progress value={curriculum.generation_progress || 0} className="h-2" />
                                <p className="text-sm text-center text-muted-foreground mt-2">
                                    {curriculum.generation_message || 'Processing...'}
                                </p>
                            </div>
                        )}
                        
                        <Accordion type="multiple" className="space-y-2">
                            {curriculum.modules.map((module, mIdx) => (
                                <AccordionItem 
                                    key={module.id} 
                                    value={module.id}
                                    className="border rounded-lg px-4"
                                >
                                    <AccordionTrigger className="hover:no-underline">
                                        <div className="flex items-center gap-3 text-left">
                                            <div className="p-2 rounded-lg bg-primary/10">
                                                <Layers className="h-4 w-4 text-primary" />
                                            </div>
                                            <div>
                                                <p className="font-medium">
                                                    Module {mIdx + 1}: {module.name}
                                                </p>
                                                <p className="text-xs text-muted-foreground">
                                                    {module.topics?.length || 0} topics
                                                </p>
                                            </div>
                                        </div>
                                    </AccordionTrigger>
                                    <AccordionContent>
                                        <div className="space-y-2 pt-2">
                                            {module.topics?.map((topic, tIdx) => (
                                                <div 
                                                    key={topic.id}
                                                    className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium">
                                                            {tIdx + 1}
                                                        </div>
                                                        <div>
                                                            <p className="text-sm font-medium">{topic.name}</p>
                                                            <p className="text-xs text-muted-foreground">
                                                                {topic.estimated_duration}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <Badge className={getStatusColor(topic.status)} variant="outline">
                                                            {topic.status === 'approved' && <CheckCircle className="h-3 w-3 mr-1" />}
                                                            {topic.status}
                                                        </Badge>
                                                        <Button 
                                                            variant="ghost" 
                                                            size="sm"
                                                            onClick={() => onViewTopic(curriculum.id, topic.id)}
                                                        >
                                                            <Eye className="h-4 w-4" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </div>
                )}
                
                {/* Learning Goals */}
                {curriculum.learning_goals?.length > 0 && (
                    <div className="mt-6 p-4 rounded-lg bg-muted/50">
                        <h4 className="font-medium mb-2 flex items-center gap-2">
                            <Target className="h-4 w-4" />
                            Learning Goals
                        </h4>
                        <ul className="space-y-1">
                            {curriculum.learning_goals.map((goal, idx) => (
                                <li key={idx} className="text-sm flex items-start gap-2">
                                    <CheckCircle className="h-4 w-4 text-emerald-500 mt-0.5" />
                                    {goal}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

export default CurriculumDetail;
